import { UploadOutlined, UserOutlined } from '@ant-design/icons';
import { useOktaAuth } from '@okta/okta-react';
import {
	Avatar,
	Button,
	Tooltip,
	Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import type { FC } from 'react';
import { useAtomValue } from 'jotai';
import { AUTH_TOKEN_STORAGE_KEY } from '@constants/auth';
import { sidebarCollapsedAtom } from '@/store/layoutAtoms';
import styles from './styles.module.scss';

const { Text } = Typography;

type OktaUser = {
	name?: string;
	email?: string;
	title?: string;
};

const SidebarUser: FC = () => {
	const { oktaAuth, authState } = useOktaAuth();
	const isSidebarCollapsed = useAtomValue(sidebarCollapsedAtom);
	const [user, setUser] = useState<OktaUser | undefined>(undefined);

	useEffect(() => {
		if (!authState?.isAuthenticated) return;

		oktaAuth
			.getUser()
			.then((u) => setUser(u as OktaUser))
			.catch(() => undefined);
	}, [oktaAuth, authState?.isAuthenticated]);

	const name = user?.name ?? '';
	const secondary = user?.title ?? user?.email ?? '';
	const initials = name
		.split(' ')
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase() ?? '')
		.join('');

	const handleSignOut = (): void => {
		// Don't leave the bridged token behind for the origin after logout.
		sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
		oktaAuth.signOut().catch(() => undefined);
	};

	return (
		<div className={`${styles.user} ${isSidebarCollapsed ? styles.userCollapsed : ''}`}>
			<div className={styles.userRow}>
				<Tooltip
					title={isSidebarCollapsed ? name : ''}
					placement="right"
				>
					<Avatar className={styles.avatar}>{initials || <UserOutlined />}</Avatar>
				</Tooltip>
				{!isSidebarCollapsed && (
					<div className={styles.userInfo}>
						<Text
							className={styles.userName}
							ellipsis
						>
							{name}
						</Text>
						{secondary && (
							<Text
								className={styles.userTitle}
								ellipsis
							>
								{secondary}
							</Text>
						)}
					</div>
				)}
			</div>
			<Tooltip
				title={isSidebarCollapsed ? 'Sign Out' : ''}
				placement="right"
			>
				<Button
					type="default"
					ghost
					className={`${styles.signOut} ${isSidebarCollapsed ? styles.signOutCollapsed : ''}`}
					aria-label="Sign Out"
					onClick={handleSignOut}
					icon={<UploadOutlined rotate={90} />}
				>
					{!isSidebarCollapsed && 'Sign Out'}
				</Button>
			</Tooltip>
		</div>
	);
};

export default SidebarUser;
